// src/pages/FilePdfPage.tsx
// Standalone PDF viewer for /Dashboard/FilePdf/#/[pdfId]
// Reads the PDF ID from the URL hash, validates permissions, and shows
// the PDF in an iframe — no MainLayout, no extra controls, right-click disabled.

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { downloadFile } from '../services/imagesGalleryService';
import './FilePdfPage.css';

interface FilePermissionsResponse {
  HasPermissions: boolean;
  Id: number;
  IsReply: boolean;
}

interface FilePdfItem {
  AttachmentId: number;
  EmbeddedImageId: number | null;
  FileName: string;
  FileUrl: string;
  FileExtension: string;
  Description: string;
  Title?: string;
}

export const FilePdfPage: React.FC = () =>
{
  const location = useLocation();

  // Hash: "#/{id}" for conversation PDFs, "#/Chat/{fileName}" for chat PDFs
  const rawHash = location.hash.replace(/^#\/?/, '').trim();
  const isFromChat = /^chat\//i.test(rawHash);
  const pdfId = isFromChat ? rawHash.replace(/^chat\//i, '').trim() : rawHash;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfItem, setPdfItem] = useState<FilePdfItem | null>(null);

  useEffect(() =>
  {
    if (!pdfId)
    {
      setError('No document ID provided.');
      setLoading(false);
      return;
    }

    const loadPdf = async () =>
    {
      setLoading(true);
      setError(null);
      try
      {
        // 1. Validate permissions (chat files require the isFromChat flag)
        const perms = await apiService.get<FilePermissionsResponse>(
          `File/GetUrlFileImageByPermissions?fileName=${pdfId}${isFromChat ? '&isFromChat=true' : ''}`,
        );

        if (!perms.HasPermissions)
        {
          setError('You do not have permission to view this document.');
          return;
        }

        // 2. Chat files use their own endpoint, which returns a single object
        if (isFromChat)
        {
          const chatFile = await apiService.get<FilePdfItem>(
            `Chat/GetFileImages?fileName=${pdfId}`,
          );

          if (!chatFile || !chatFile.FileUrl)
          {
            setError('Document not found.');
            return;
          }

          setPdfItem(chatFile);
          return;
        }

        const attachments = await apiService.get<FilePdfItem[]>(
          `Conversation/GetAttachmentByName?name=${pdfId}`,
        );

        if (!attachments || attachments.length === 0)
        {
          setError('Document not found.');
          return;
        }

        setPdfItem(attachments[0] ?? null);
      }
      catch (err)
      {
        console.error('FilePdfPage: failed to load PDF', err);
        setError('Failed to load document. Please make sure you are logged in.');
      }
      finally
      {
        setLoading(false);
      }
    };

    loadPdf();
  }, [pdfId, isFromChat]);

  const handleDownload = useCallback(
    async (e: React.MouseEvent) =>
    {
      e.preventDefault();
      if (!pdfItem) return;
      const fileName =
        (pdfItem.Description || pdfItem.FileName || 'document') + (pdfItem.FileExtension || '');
      try
      {
        await downloadFile(
          pdfItem.AttachmentId,
          pdfItem.EmbeddedImageId ?? 0,
          fileName,
          pdfItem.FileUrl,
        );
      }
      catch (err)
      {
        console.error('FilePdfPage: download failed', err);
      }
    },
    [pdfItem],
  );

  if (loading)
  {
    return (
      <div className="fp-page">
        <div className="fp-spinner" />
      </div>
    );
  }

  if (error || !pdfItem)
  {
    return (
      <div className="fp-page fp-page--error">
        <i className="material-icons fp-error-icon">picture_as_pdf</i>
        <p className="fp-error-text">{error || 'Document not available.'}</p>
      </div>
    );
  }

  return (
    <div className="fp-page" onContextMenu={e => e.preventDefault()}>
      <div className="fp-card">
        {/* PDF iframe */}
        <div className="fp-viewer-wrapper">
          <iframe
            className="fp-iframe"
            src={pdfItem.FileUrl}
            title={pdfItem.Description || pdfItem.FileName}
          />
        </div>

        {/* Footer: description + download */}
        <div className="fp-footer">
          <span className="fp-description">{pdfItem.Description || pdfItem.FileName || ''}</span>
          <a href="#" className="fp-download-link" onClick={handleDownload}>
            Download
          </a>
        </div>
      </div>
    </div>
  );
};

export default FilePdfPage;
